import { useToast } from '@shared/components/toast';
import {
  GetOrCreateConversationWithConciergeDocument,
  useAddChatMessageToConversationMutation,
  useCreateNewConversationWithConciergeMutation,
  useGetConversationExternalMessagesQuery,
  useGetOrCreateConversationWithConciergeQuery,
  useHasConciergeActionDefinitionQuery,
} from '@shared/generated/graphql';
import { writeConversationToCache } from '@shared/graphql/cache/conversationCache';
import { toConversationMessageEvents } from '@shared/graphql/fromFragments/externalMessage';
import { useConciergeContext } from '@shared/plugin/contexts/ConciergeContext';
import { ChatViewContext } from 'clerk_common';

type UseChatWithConciergeProps = {
  organizationId: string;
};
export function useChatWithConcierge({
  organizationId,
}: UseChatWithConciergeProps) {
  const { data } = useGetOrCreateConversationWithConciergeQuery({
    variables: { input: { organizationId } },
  });
  const { sendToast } = useToast();
  const { data: hasConciergeData } = useHasConciergeActionDefinitionQuery({
    variables: { input: { organizationId } },
    pollInterval: 30 * 1000,
  });

  const [
    createNewConversationWithConcierge,
    { loading: isCreatingConversation },
  ] = useCreateNewConversationWithConciergeMutation({
    update: (cache, { data }) => {
      const conversation =
        data?.createNewConversationWithConcierge.conversation;
      if (conversation) writeConversationToCache(cache, conversation);
    },
  });

  const hasAccessToConcierge = Boolean(
    hasConciergeData?.hasConciergeActionDefinition.hasConcierge
  );

  const createNewConversation = async (
    context: ChatViewContext
  ): Promise<string> => {
    if (!hasAccessToConcierge) {
      return '';
    }
    const result = await createNewConversationWithConcierge({
      variables: {
        input: {
          organizationId,
          context,
        },
      },
      refetchQueries: [
        {
          query: GetOrCreateConversationWithConciergeDocument,
          variables: { input: { organizationId } },
        },
      ],
    });
    return (
      result.data?.createNewConversationWithConcierge.conversation.id || ''
    );
  };

  const [addChatMessageToConversation, { loading: isAddingMessage }] =
    useAddChatMessageToConversationMutation();

  const sendMessageToConcierge = async (message: string) => {
    if (!hasAccessToConcierge) {
      return;
    }
    if (!data?.getOrCreateConversationWithConcierge.id) {
      throw new Error('Conversation ID not found');
    }
    try {
      await addChatMessageToConversation({
        variables: {
          input: {
            conversationId: data?.getOrCreateConversationWithConcierge.id,
            message,
          },
        },
      });
    } catch (error) {
      sendToast('Failed to send message to concierge', {
        variant: 'error',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      console.error(error);
    }
  };
  return {
    conciergeConversationId: data?.getOrCreateConversationWithConcierge.id,
    createNewConversation,
    isCreatingConversation,
    sendMessageToConcierge,
    isAddingMessage,
    hasAccessToConcierge,
  };
}

type UseChatWithConciergeFeedProps = {
  conciergeConversationId: string;
};
export function useChatWithConciergeFeed({
  conciergeConversationId,
}: UseChatWithConciergeFeedProps) {
  const { hasAccessToConcierge, organizationId } = useConciergeContext();
  const shouldPoll = hasAccessToConcierge && conciergeConversationId;
  const { data } = useGetConversationExternalMessagesQuery({
    variables: {
      input: {
        conversationId: conciergeConversationId,
        organizationIds: [organizationId],
      },
    },
    // NOTE(parlato): We should definitely not be polling the external
    // messages table every second
    pollInterval: shouldPoll ? 1 * 1000 : undefined,
  });
  return {
    messages: toConversationMessageEvents(data),
  };
}
